import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash2, X, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Suppliers() {
  const { user } = useAuth();
  const canEdit = ["admin", "production"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [show, setShow] = useState(false);

  const load = async () => { const r = await api.get("/suppliers"); setItems(r.data); };
  useEffect(() => { load().catch(() => {}); }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Master Data</div>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-950">Suppliers</h1>
        </div>
        {canEdit && <button data-testid="new-supplier-btn" onClick={() => { setEditing(null); setShow(true); }} className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 text-sm uppercase tracking-wider font-medium flex items-center gap-2"><Plus size={16} /> New Supplier</button>}
      </div>

      <div className="bg-white border border-zinc-300 overflow-x-auto" data-testid="suppliers-table">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-zinc-100 text-zinc-600 uppercase text-[10px] font-mono tracking-wider">
            <tr>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Name</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Material Supplied</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Contact</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Phone</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Email</th>
              {canEdit && <th className="border-b border-zinc-300"></th>}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={6} className="text-center text-zinc-500 py-12 text-sm">No suppliers yet. Add granule and packaging suppliers.</td></tr>
            ) : items.map((s) => (
              <tr key={s.id} className="border-b border-zinc-200 hover:bg-zinc-50">
                <td className="px-3 py-2 font-semibold text-zinc-900">{s.name}</td>
                <td className="px-3 py-2">{s.material || "—"}</td>
                <td className="px-3 py-2">{s.contact_person || "—"}</td>
                <td className="px-3 py-2 font-mono text-xs">{s.phone || "—"}</td>
                <td className="px-3 py-2 font-mono text-xs">{s.email || "—"}</td>
                {canEdit && (
                  <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                    <button data-testid={`edit-supplier-${s.id}`} onClick={() => { setEditing(s); setShow(true); }} className="text-zinc-600 hover:text-zinc-900"><Pencil size={14} /></button>
                    {user?.role === "admin" && (
                      <button data-testid={`delete-supplier-${s.id}`} onClick={async () => {
                        if (!window.confirm(`Delete ${s.name}?`)) return;
                        try { await api.delete(`/suppliers/${s.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
                      }} className="text-red-600 hover:text-red-800"><Trash2 size={14} /></button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {show && <SupplierForm editing={editing} onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
    </div>
  );
}

function SupplierForm({ editing, onClose, onSaved }) {
  const [form, setForm] = useState(editing || { name: "", material: "", contact_person: "", phone: "", email: "", address: "", gst: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) await api.put(`/suppliers/${editing.id}`, form);
      else await api.post("/suppliers", form);
      toast.success(editing ? "Updated" : "Created");
      onSaved();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const cls = "w-full h-9 px-2 text-sm border border-zinc-300 bg-white font-mono";
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-10" data-testid="supplier-form-modal">
      <div className="bg-white border border-zinc-300 w-full max-w-2xl">
        <div className="flex items-center justify-between border-b border-zinc-300 px-6 py-4">
          <h2 className="text-xl font-semibold">{editing ? "Edit" : "New"} Supplier</h2>
          <button onClick={onClose}><X /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[10px] font-semibold uppercase mb-1 block">Name *</label><input data-testid="sup-name" required value={form.name} onChange={f("name")} className={cls} /></div>
            <div><label className="text-[10px] font-semibold uppercase mb-1 block">Material Supplied</label><input data-testid="sup-material" value={form.material} onChange={f("material")} className={cls} placeholder="LLDPE, Cores, Boxes" /></div>
            <div><label className="text-[10px] font-semibold uppercase mb-1 block">Contact Person</label><input data-testid="sup-contact" value={form.contact_person} onChange={f("contact_person")} className={cls} /></div>
            <div><label className="text-[10px] font-semibold uppercase mb-1 block">Phone</label><input data-testid="sup-phone" value={form.phone} onChange={f("phone")} className={cls} /></div>
            <div><label className="text-[10px] font-semibold uppercase mb-1 block">Email</label><input data-testid="sup-email" type="email" value={form.email} onChange={f("email")} className={cls} /></div>
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase mb-1 block">Address</label>
            <textarea data-testid="sup-address" value={form.address} onChange={f("address")} rows={2} className="w-full p-2 text-sm border border-zinc-300 bg-white font-mono" />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase mb-1 block">GSTIN</label>
            <input data-testid="sup-gst" value={form.gst || ""} onChange={f("gst")} className="w-full h-9 px-2 text-sm border border-zinc-300 bg-white font-mono" placeholder="e.g. 27AAACR5055K1Z5" />
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm uppercase border border-zinc-300 bg-white">Cancel</button>
            <button data-testid="save-supplier-btn" type="submit" disabled={busy} className="px-5 py-2 text-sm uppercase bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-60">{busy ? "..." : "Save"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
